/**
 * Holiday Timeline Component
 * Handles card expansion/collapse for holiday timeline
 */

(function ($, Drupal, once) {
  'use strict';

  const trackedSwipeGroups = [];
  let swipeListenerAttached = false;

  function debounce(func, wait) {
    let timeoutId;
    return function debounced() {
      const context = this;
      const args = arguments;

      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(context, args), wait);
    };
  }

  function isInViewport(element) {
    if (typeof window === 'undefined' || !element) {
      return false;
    }

    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;

    return rect.top < windowHeight && rect.bottom > 0;
  }

  function updateSwipeHints() {
    if (!trackedSwipeGroups.length) {
      return;
    }

    // This function is called on page scroll
    // We add the swipe class to hide the hint when component is in viewport
    // and user is scrolling (indicating they're interacting with the page)
    trackedSwipeGroups.forEach((group) => {
      if (!group.classList.contains('swipe') && isInViewport(group)) {
        // User is scrolling the page and component is visible, hide the hint
        group.classList.add('swipe');
      }
    });
  }

  function initSwipeHint() {
    if (typeof window === 'undefined') {
      return;
    }

    const sliderGroups = document.querySelectorAll('.holiday-timeline.component');

    sliderGroups.forEach((group) => {
      if (trackedSwipeGroups.indexOf(group) === -1) {
        trackedSwipeGroups.push(group);
      }
    });

    if (!trackedSwipeGroups.length) {
      return;
    }

    if (!swipeListenerAttached) {
      const handleScroll = debounce(updateSwipeHints, 100);
      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', handleScroll, { passive: true });
      swipeListenerAttached = true;
    }

    // Initial check
    updateSwipeHints();
    
    // Also hide swipe hint after 5 seconds as a fallback
    setTimeout(() => {
      trackedSwipeGroups.forEach((group) => {
        if (!group.classList.contains('swipe')) {
          group.classList.add('swipe');
        }
      });
    }, 5000);
  }

  let initialized = false;

  function initDragScroll(track) {
    let isDown = false;
    let startX;
    let scrollLeft;
    let hasMoved = false;
    const component = track.closest('.holiday-timeline.component');

    track.addEventListener('mousedown', (e) => {
      // Don't interfere with toggle button clicks
      if (e.target.closest('.holiday-timeline__toggle')) {
        return;
      }

      isDown = true;
      hasMoved = false;
      track.classList.add('is-dragging');
      startX = e.pageX - track.offsetLeft;
      scrollLeft = track.scrollLeft;
      track.style.cursor = 'grabbing';
      track.style.userSelect = 'none';

      // Hide swipe hint when user starts dragging
      if (component && !component.classList.contains('swipe')) {
        component.classList.add('swipe');
      }
    });

    track.addEventListener('mouseleave', () => {
      isDown = false;
      track.classList.remove('is-dragging');
      track.style.cursor = 'grab';
    });

    track.addEventListener('mouseup', () => {
      isDown = false;
      track.classList.remove('is-dragging');
      track.style.cursor = 'grab';
    });

    track.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      hasMoved = true;
      const x = e.pageX - track.offsetLeft;
      const walk = (x - startX) * 2; // Scroll speed multiplier
      track.scrollLeft = scrollLeft - walk;
    });

    // Hide swipe hint on scroll
    track.addEventListener('scroll', () => {
      if (component && !component.classList.contains('swipe')) {
        component.classList.add('swipe');
      }
    }, { passive: true });

    // Hide swipe hint on touch
    track.addEventListener('touchstart', () => {
      if (component && !component.classList.contains('swipe')) {
        component.classList.add('swipe');
      }
    }, { passive: true });

    // Prevent click events when dragging
    track.addEventListener('click', (e) => {
      if (hasMoved && !e.target.closest('.holiday-timeline__toggle')) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);

    // Set initial cursor
    track.style.cursor = 'grab';
  }

  function initTimeline() {
    if (initialized) {
      return;
    }

    initialized = true;

    const components = document.querySelectorAll('.holiday-timeline');

    components.forEach((component) => {
      const track = component.querySelector('.holiday-timeline__track');
      const cards = component.querySelectorAll('.holiday-timeline__card:not(.holiday-timeline__card--intro)');
      const toggles = component.querySelectorAll('.holiday-timeline__toggle');

      // Initialize drag scroll
      if (track) {
        initDragScroll(track);
      }

      toggles.forEach((toggle, index) => {
        const card = toggle.closest('.holiday-timeline__card');
        const body = card.querySelector('.holiday-timeline__body');

        // Update toggle icon based on state
        function updateToggleIcon(isOpen) {
          const icon = toggle.querySelector('span');
          if (icon) {
            icon.textContent = isOpen ? '−' : '+';
          }
        }

        // Set initial state - first holiday card open (index 0 after intro)
        // if (index === 0) {
        //   card.classList.add('is-open');
        //   toggle.setAttribute('aria-expanded', 'true');
        //   updateToggleIcon(true);
        //   if (body) {
        //     body.setAttribute('aria-hidden', 'false');
        //     body.style.display = 'block';
        //   }
        // } else {
          toggle.setAttribute('aria-expanded', 'false');
          updateToggleIcon(false);
          if (body) {
            body.setAttribute('aria-hidden', 'true');
            body.style.display = 'none';
          }
        //}

        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const isCurrentlyOpen = card.classList.contains('is-open');
          const willBeOpen = !isCurrentlyOpen;

          // Toggle the current card
          if (willBeOpen) {
            card.classList.add('is-open');
          } else {
            card.classList.remove('is-open');
          }

          toggle.setAttribute('aria-expanded', String(willBeOpen));
          updateToggleIcon(willBeOpen);

          if (body) {
            body.setAttribute('aria-hidden', String(!willBeOpen));
            body.style.display = willBeOpen ? 'block' : 'none';
          }

          // Close other cards when opening a new one
          if (willBeOpen) {
            cards.forEach((otherCard) => {
              if (otherCard !== card && !otherCard.classList.contains('holiday-timeline__card--intro')) {
                otherCard.classList.remove('is-open');
                const otherToggle = otherCard.querySelector('.holiday-timeline__toggle');
                const otherBody = otherCard.querySelector('.holiday-timeline__body');

                if (otherToggle) {
                  otherToggle.setAttribute('aria-expanded', 'false');
                  const otherIcon = otherToggle.querySelector('span');
                  if (otherIcon) {
                    otherIcon.textContent = '+';
                  }
                }

                if (otherBody) {
                  otherBody.setAttribute('aria-hidden', 'true');
                  otherBody.style.display = 'none';
                }
              }
            });

            // Scroll the opened card into view
            setTimeout(() => {
              card.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center'
              });
            }, 100);
          }
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initTimeline();
      initSwipeHint();
    });
  } else {
    initTimeline();
    initSwipeHint();
  }
})(jQuery, Drupal, once);
